import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import DashboardLayout from '../layout/Dashboard';
import { getOrganizations } from '../../api/clients/organizations';
import {
  getProductGroups,
  getSalesOrganizations,
  getSalesPeriods,
  getSalesProducts,
  getSalesSummary
} from '../../api/admin/reports';
import { ACL } from '../../config/constants/UogACL/ACL';
import { Messages } from '../../components/layout/Messages/Messages';
import { NoRights } from '../../components/layout/Permissions/NoRights';
import { store } from '../../store/store';
import { hideNotification } from '../../store/actions/notification';
import {
  EntityTabLayout,
  PageHeader,
  PageSurface,
  PageWorkspace
} from '../../components/layout/Workspace';
import {
  createDefaultReportFilters,
  parseReportQuery,
  reportFiltersEqual,
  serializeReportQuery,
  validateReportFilters
} from './reportFilters';
import { ReportFilters } from './components/ReportFilters';
import { OrganizationsReport } from './components/OrganizationsReport';
import { ProductsReport } from './components/ProductsReport';
import { PeriodsReport } from './components/PeriodsReport';
import { ReportPeriodContext } from './components/ReportPeriodContext';
import { SummaryReport } from './components/SummaryReport';
import {
  catalogEntityIdentifiers,
  isArchiveProductGroup,
  resolveCatalogLabel,
  resolveProductGroupSection,
  resolveProductCatalogLabel
} from './reportCatalogLabels';
import { reconcileProductCatalogFilters } from './reportProductCascade';
import { resolveReportChartSeriesScope } from './reportChartSeriesScope';
import './ListReports.scss';

function optionFromEntity(entity) {
  return entity && entity.id ? { value: String(entity.id), label: entity.name || String(entity.id) } : null;
}

function normalizeCatalogs(organizationsResponse, productGroupsResponse, locale = 'ro', formatMessage) {
  const organizations = Array.isArray(organizationsResponse && organizationsResponse.data)
    ? organizationsResponse.data
    : [];
  const productGroups = Array.isArray(productGroupsResponse && productGroupsResponse.data)
    ? productGroupsResponse.data
    : [];

  const collator = new Intl.Collator(locale, { sensitivity: 'base' });
  const normalizedGroups = productGroups.map(group => {
    if (!group || !group.id) {
      return null;
    }

    const archive = isArchiveProductGroup(group);
    const section = resolveProductGroupSection(group, locale, formatMessage);
    return {
      archive,
      categoryKey: section.key,
      categoryLabel: section.label,
      categoryRank: section.rank,
      entity: group,
      identifiers: catalogEntityIdentifiers(group),
      label: resolveCatalogLabel(group, locale, formatMessage),
      value: String(group.id)
    };
  }).filter(Boolean).sort((left, right) => (
    left.categoryRank - right.categoryRank
      || collator.compare(left.label, right.label)
      || left.value.localeCompare(right.value)
  ));
  const products = normalizedGroups.reduce((options, group) => [
    ...options,
    ...(Array.isArray(group.entity.products) ? group.entity.products.map(product => ({
      archive: group.archive,
      categoryKey: group.categoryKey,
      categoryLabel: group.categoryLabel,
      categoryRank: group.categoryRank,
      groupId: group.value,
      groupLabel: group.label,
      identifiers: catalogEntityIdentifiers(product),
      label: resolveCatalogLabel(product, locale, formatMessage),
      selectedLabel: resolveProductCatalogLabel(product, group.entity, locale, formatMessage),
      value: String(product.id)
    })) : [])
  ], []).sort((left, right) => (
    left.categoryRank - right.categoryRank
      || collator.compare(left.groupLabel, right.groupLabel)
      || collator.compare(left.label, right.label)
      || left.value.localeCompare(right.value)
  ));

  return {
    organizations: organizations.map(optionFromEntity).filter(Boolean),
    productGroups: normalizedGroups.map(({ entity, categoryRank, ...option }) => option),
    products: products.map(({ categoryRank, ...option }) => option)
  };
}

function reportTableEqual(left, right) {
  return left.page === right.page
    && left.perPage === right.perPage
    && left.sortBy === right.sortBy
    && left.sortDirection === right.sortDirection;
}

function ListReports(props) {
  const intl = useIntl();
  const hasAccess = ACL.CLIENT.REPORTS.LIST.hasAccess();
  const { hideSpinner } = props;
  const locationSearch = props.location && props.location.search;
  const initialQuery = useMemo(
    () => parseReportQuery(locationSearch),
    // The route creates this page once; subsequent URL changes are reconciled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const initialActiveTab = ['summary', 'products', 'periods', 'organizations'].includes(initialQuery.activeTab)
    ? initialQuery.activeTab
    : 'summary';
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  const [periodLens, setPeriodLens] = useState(initialQuery.periodLens);
  const [comparisonVisibility, setComparisonVisibility] = useState({
    financial: false,
    products: false
  });
  const [appliedFilters, setAppliedFilters] = useState(initialQuery.filters);
  const [draftFilters, setDraftFilters] = useState(initialQuery.filters);
  const [productTable, setProductTable] = useState(initialQuery.productTable);
  const [organizationTable, setOrganizationTable] = useState(initialQuery.organizationTable);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(hasAccess && initialActiveTab === 'summary');
  const [summaryError, setSummaryError] = useState(null);
  const [products, setProducts] = useState(null);
  const [productsLoading, setProductsLoading] = useState(hasAccess && initialActiveTab === 'products');
  const [productsError, setProductsError] = useState(null);
  const [periods, setPeriods] = useState(null);
  const [periodsLoading, setPeriodsLoading] = useState(hasAccess && initialActiveTab === 'periods');
  const [periodsError, setPeriodsError] = useState(null);
  const [organizations, setOrganizations] = useState(null);
  const [organizationsLoading, setOrganizationsLoading] = useState(hasAccess && initialActiveTab === 'organizations');
  const [organizationsError, setOrganizationsError] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(hasAccess);
  const [catalogReady, setCatalogReady] = useState(false);
  const [catalogResponses, setCatalogResponses] = useState({ organizations: null, productGroups: null });
  const catalogs = useMemo(() => normalizeCatalogs(
    catalogResponses.organizations,
    catalogResponses.productGroups,
    intl.locale || 'ro',
    intl.formatMessage
  ), [catalogResponses, intl.formatMessage, intl.locale]);
  const chartSeriesScope = useMemo(
    () => resolveReportChartSeriesScope(appliedFilters, catalogs),
    [appliedFilters, catalogs]
  );
  const summaryAbortRef = useRef(null);
  const summaryDataKeyRef = useRef(null);
  const productsAbortRef = useRef(null);
  const periodsAbortRef = useRef(null);
  const organizationsAbortRef = useRef(null);
  const lastLocationSearchRef = useRef(locationSearch);

  const loadSummary = useCallback(filters => {
    if (!hasAccess) {
      return;
    }

    if (summaryAbortRef.current) {
      summaryAbortRef.current.abort();
    }
    const controller = new AbortController();
    summaryAbortRef.current = controller;
    setSummaryLoading(true);
    setSummaryError(null);

    const requestKey = JSON.stringify(filters);
    getSalesSummary(filters, controller.signal)
      .then(response => {
        if (!controller.signal.aborted) {
          setSummary(response && response.data ? response.data : null);
          summaryDataKeyRef.current = requestKey;
        }
      })
      .catch(requestError => {
        if (!controller.signal.aborted && requestError.code !== 'ERR_CANCELED') {
          setSummaryError(requestError);
          setSummary(null);
          summaryDataKeyRef.current = null;
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setSummaryLoading(false);
          if (hideSpinner) {
            hideSpinner();
          }
        }
      });
  }, [hasAccess, hideSpinner]);

  const loadProducts = useCallback((filters, table) => {
    if (!hasAccess) {
      return;
    }

    if (productsAbortRef.current) {
      productsAbortRef.current.abort();
    }
    const controller = new AbortController();
    productsAbortRef.current = controller;
    setProductsLoading(true);
    setProductsError(null);

    getSalesProducts(filters, table, controller.signal)
      .then(response => {
        if (!controller.signal.aborted) {
          setProducts(response && response.data ? response.data : null);
        }
      })
      .catch(requestError => {
        if (!controller.signal.aborted && requestError.code !== 'ERR_CANCELED') {
          setProductsError(requestError);
          setProducts(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setProductsLoading(false);
          if (hideSpinner) {
            hideSpinner();
          }
        }
      });
  }, [hasAccess, hideSpinner]);

  const loadPeriods = useCallback(filters => {
    if (!hasAccess) {
      return;
    }

    if (periodsAbortRef.current) {
      periodsAbortRef.current.abort();
    }
    const controller = new AbortController();
    periodsAbortRef.current = controller;
    setPeriodsLoading(true);
    setPeriodsError(null);

    getSalesPeriods(filters, controller.signal)
      .then(response => {
        if (!controller.signal.aborted) {
          setPeriods(response && response.data ? response.data : null);
        }
      })
      .catch(requestError => {
        if (!controller.signal.aborted && requestError.code !== 'ERR_CANCELED') {
          setPeriodsError(requestError);
          setPeriods(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setPeriodsLoading(false);
          if (hideSpinner) {
            hideSpinner();
          }
        }
      });
  }, [hasAccess, hideSpinner]);

  const loadOrganizations = useCallback((filters, table) => {
    if (!hasAccess) {
      return;
    }

    if (organizationsAbortRef.current) {
      organizationsAbortRef.current.abort();
    }
    const controller = new AbortController();
    organizationsAbortRef.current = controller;
    setOrganizationsLoading(true);
    setOrganizationsError(null);

    getSalesOrganizations(filters, table, controller.signal)
      .then(response => {
        if (!controller.signal.aborted) {
          setOrganizations(response && response.data ? response.data : null);
        }
      })
      .catch(requestError => {
        if (!controller.signal.aborted && requestError.code !== 'ERR_CANCELED') {
          setOrganizationsError(requestError);
          setOrganizations(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setOrganizationsLoading(false);
          if (hideSpinner) {
            hideSpinner();
          }
        }
      });
  }, [hasAccess, hideSpinner]);

  useEffect(() => {
    if (!hasAccess) {
      return undefined;
    }

    if (activeTab !== 'summary') {
      return undefined;
    }

    loadSummary(appliedFilters);
    return () => {
      if (summaryAbortRef.current) {
        summaryAbortRef.current.abort();
      }
    };
  }, [activeTab, appliedFilters, hasAccess, loadSummary]);

  useEffect(() => {
    if (!hasAccess || activeTab !== 'periods'
      || summaryDataKeyRef.current === JSON.stringify(appliedFilters)) {
      return undefined;
    }
    loadSummary(appliedFilters);
    return () => summaryAbortRef.current && summaryAbortRef.current.abort();
  }, [activeTab, appliedFilters, hasAccess, loadSummary]);

  useEffect(() => {
    if (!hasAccess || activeTab !== 'products') {
      return undefined;
    }

    loadProducts(appliedFilters, productTable);
    return () => {
      if (productsAbortRef.current) {
        productsAbortRef.current.abort();
      }
    };
  }, [activeTab, appliedFilters, hasAccess, loadProducts, productTable]);

  useEffect(() => {
    if (!hasAccess || activeTab !== 'periods') {
      return undefined;
    }

    loadPeriods(appliedFilters);
    return () => {
      if (periodsAbortRef.current) {
        periodsAbortRef.current.abort();
      }
    };
  }, [activeTab, appliedFilters, hasAccess, loadPeriods]);

  useEffect(() => {
    if (!hasAccess || activeTab !== 'organizations') {
      return undefined;
    }

    loadOrganizations(appliedFilters, organizationTable);
    return () => {
      if (organizationsAbortRef.current) {
        organizationsAbortRef.current.abort();
      }
    };
  }, [activeTab, appliedFilters, hasAccess, loadOrganizations, organizationTable]);

  useEffect(() => {
    if (!hasAccess) {
      return undefined;
    }

    let mounted = true;
    Promise.all([
      getOrganizations({ disablePagination: true }),
      getProductGroups({ disablePagination: true })
    ])
      .then(([organizationsResponse, productGroupsResponse]) => {
        if (mounted) {
          setCatalogResponses({
            organizations: organizationsResponse,
            productGroups: productGroupsResponse
          });
          setCatalogReady(Boolean(
            productGroupsResponse && Array.isArray(productGroupsResponse.data)
          ));
        }
      })
      .catch(() => {
        // Catalogue filters are optional to the summary result. Their API
        // wrappers already surface the request error; keep manual date/type
        // filtering and the report itself usable.
      })
      .finally(() => {
        if (mounted) {
          setCatalogLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [hasAccess]);

  useEffect(() => {
    const nextSearch = locationSearch;
    if (nextSearch === lastLocationSearchRef.current) {
      return;
    }

    lastLocationSearchRef.current = nextSearch;
    const parsed = parseReportQuery(nextSearch);
    setActiveTab(['summary', 'products', 'periods', 'organizations'].includes(parsed.activeTab) ? parsed.activeTab : 'summary');
    setPeriodLens(parsed.periodLens);
    if (!reportTableEqual(parsed.productTable, productTable)) {
      setProductTable(parsed.productTable);
    }
    if (!reportTableEqual(parsed.organizationTable, organizationTable)) {
      setOrganizationTable(parsed.organizationTable);
    }
    if (!reportFiltersEqual(parsed.filters, appliedFilters)) {
      setAppliedFilters(parsed.filters);
      setDraftFilters(parsed.filters);
    }
  }, [appliedFilters, locationSearch, organizationTable, productTable]);

  useEffect(() => () => store.dispatch(hideNotification()), []);

  const validationError = validateReportFilters(draftFilters);
  const dirty = !reportFiltersEqual(draftFilters, appliedFilters);

  const updateUrl = useCallback((
    tab,
    filters,
    productState = productTable,
    lens = periodLens,
    organizationState = organizationTable
  ) => {
    const search = serializeReportQuery(filters, tab, productState, lens, organizationState);
    if (props.history && props.history.push) {
      props.history.push({
        pathname: props.location && props.location.pathname ? props.location.pathname : '/reports',
        search
      });
    }
  }, [organizationTable, periodLens, productTable, props.history, props.location]);

  useEffect(() => {
    if (!catalogReady) {
      return;
    }

    const nextAppliedFilters = reconcileProductCatalogFilters(
      appliedFilters,
      catalogs.products,
      catalogs.productGroups
    );
    const nextDraftFilters = reconcileProductCatalogFilters(
      draftFilters,
      catalogs.products,
      catalogs.productGroups
    );
    const appliedChanged = !reportFiltersEqual(nextAppliedFilters, appliedFilters);
    const draftChanged = !reportFiltersEqual(nextDraftFilters, draftFilters);

    if (appliedChanged) {
      setAppliedFilters(nextAppliedFilters);
      updateUrl(
        activeTab,
        nextAppliedFilters,
        productTable,
        periodLens,
        organizationTable
      );
    }
    if (draftChanged) {
      setDraftFilters(nextDraftFilters);
    }
  }, [
    activeTab,
    appliedFilters,
    catalogReady,
    catalogs.productGroups,
    catalogs.products,
    draftFilters,
    organizationTable,
    periodLens,
    productTable,
    updateUrl
  ]);

  const applyFilters = () => {
    if (validationError) {
      return;
    }

    const nextProductTable = { ...productTable, page: 1 };
    const nextOrganizationTable = { ...organizationTable, page: 1 };
    setAppliedFilters(draftFilters);
    setProductTable(nextProductTable);
    setOrganizationTable(nextOrganizationTable);
    updateUrl(activeTab, draftFilters, nextProductTable, periodLens, nextOrganizationTable);
  };

  const resetFilters = () => {
    setDraftFilters(createDefaultReportFilters());
  };

  const changeTab = tab => {
    if (!['summary', 'products', 'periods', 'organizations'].includes(tab)) {
      return;
    }

    setActiveTab(tab);
    updateUrl(tab, appliedFilters, productTable);
  };

  const changeProductTable = table => {
    setProductTable(table);
    updateUrl('products', appliedFilters, table);
  };

  const changeOrganizationTable = table => {
    setOrganizationTable(table);
    updateUrl('organizations', appliedFilters, productTable, periodLens, table);
  };

  const changePeriodLens = lens => {
    setPeriodLens(lens);
    updateUrl('periods', appliedFilters, productTable, lens);
  };

  const filters = (
    <ReportFilters
      catalogLoading={catalogLoading}
      dirty={dirty}
      filters={draftFilters}
      onApply={applyFilters}
      onChange={setDraftFilters}
      onReset={resetFilters}
      options={catalogs}
      validationError={validationError}
    />
  );

  const tabs = [
    {
      id: 'summary',
      label: <FormattedMessage id="components.pages.reports.tabs.summary"/>,
      content: (
        <>
          {filters}
          <ReportPeriodContext hidden={summaryLoading || Boolean(summaryError)} meta={summary && summary.meta}/>
          <SummaryReport
            chartSeriesScope={chartSeriesScope}
            comparisonMode={appliedFilters.compare}
            data={summary}
            error={summaryError}
            financialComparisonEnabled={comparisonVisibility.financial}
            loading={summaryLoading}
            onFinancialComparisonChange={enabled => setComparisonVisibility(current => ({
              ...current,
              financial: enabled
            }))}
            onProductComparisonChange={enabled => setComparisonVisibility(current => ({
              ...current,
              products: enabled
            }))}
            onRetry={() => loadSummary(appliedFilters)}
            periodPreset={appliedFilters.preset}
            productComparisonEnabled={comparisonVisibility.products}
          />
        </>
      )
    },
    {
      id: 'products',
      label: <FormattedMessage id="components.pages.reports.tabs.products"/>,
      content: (
        <>
          {filters}
          <ReportPeriodContext hidden={productsLoading || Boolean(productsError)} meta={products && products.meta}/>
          <ProductsReport
            data={products}
            error={productsError}
            loading={productsLoading}
            onRetry={() => loadProducts(appliedFilters, productTable)}
            onTableChange={changeProductTable}
            table={productTable}
          />
        </>
      )
    },
    {
      id: 'periods',
      label: <FormattedMessage id="components.pages.reports.tabs.periods"/>,
      content: (
        <>
          {filters}
          <ReportPeriodContext hidden={periodsLoading || Boolean(periodsError)} meta={periods && periods.meta}/>
          <PeriodsReport
            activityData={periods && periods.activity}
            activityError={periodsError}
            activityLoading={periodsLoading}
            data={periods}
            error={periodsError}
            lens={periodLens}
            loading={periodsLoading}
            onLensChange={changePeriodLens}
            onRetry={() => loadPeriods(appliedFilters)}
            periodPreset={appliedFilters.preset}
            purchaseData={summary}
            purchaseError={summaryError}
            purchaseLoading={summaryLoading || summaryDataKeyRef.current !== JSON.stringify(appliedFilters)}
          />
        </>
      )
    },
    {
      id: 'organizations',
      label: <FormattedMessage id="components.pages.reports.tabs.organizations"/>,
      content: (
        <>
          {filters}
          <ReportPeriodContext hidden={organizationsLoading || Boolean(organizationsError)} meta={organizations && organizations.meta}/>
          <OrganizationsReport
            data={organizations}
            error={organizationsError}
            loading={organizationsLoading}
            onRetry={() => loadOrganizations(appliedFilters, organizationTable)}
            onTableChange={changeOrganizationTable}
            table={organizationTable}
          />
        </>
      )
    }
  ];

  return (
    <DashboardLayout pageTitle={<FormattedMessage id="app.dashboard.page_title"/>} workspace {...props}>
      <PageWorkspace className="reports-workspace">
        <PageHeader title={<FormattedMessage id="navigation.reports.detailed"/>}/>
        {hasAccess ? (
          <EntityTabLayout
            activeTab={activeTab}
            ariaLabel="Report sections"
            className="reports-tab-layout"
            onTabChange={changeTab}
            tabs={tabs}
          />
        ) : (
          <PageSurface className="reports-no-rights-surface">
            <Messages/>
            <NoRights/>
          </PageSurface>
        )}
      </PageWorkspace>
    </DashboardLayout>
  );
}

export { ListReports, normalizeCatalogs };
